import React from "react";
import { Flex, Text, useBreakpointValue } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useGetPathPickUsage from "@/features/pathpick/hooks/query/useGetPathPickUsage";
import useMembership from "@/shared/hooks/useMembership";
import usePathPickSelectionBarHeight from "@/features/pathpick/hooks/usePathPickSelectionBarHeight";
import {
  PATH_PICK_SELECTION_BAR_BOTTOM,
  PATH_PICK_SELECTION_BAR_Z_INDEX,
} from "@/features/pathpick/constants";
import PathPickBasketOrderLimitTooltip from "@/features/pathpick/features/basket/components/order/PathPickBasketOrderLimitTooltip";

interface Props {
  selectedCount: number;
  // 선택에 포함된 품절 상품 수 — 0보다 크면 신청/주문을 막고 안내를 노출한다.
  soldOutCount: number;
  // 선택이 일괄 주문 한도를 넘었는지 — 넘으면 주문을 막고 안내 툴팁을 띄운다.
  isOrderOverLimit: boolean;
  // 일괄 주문 대상 상품을 조회하는 동안 주문 버튼을 로딩으로 막는다.
  isOrderLoading: boolean;
  // 선택에서 품절 상품만 뺀다 — 품절이 섞여 있을 때만 활성화된다.
  onExcludeSoldOut: () => void;
  // 선택 상품 삭제 — 모바일에서는 목록 헤더 대신 바 좌측 하단에 둔다.
  onDeleteSelected: () => void;
  onApiSync: () => void;
  onFileRequest: () => void;
  onOrder: () => void;
}

interface StatBoxProps {
  label: string;
  value: string;
}

const StatBox = ({ label, value }: StatBoxProps) => {
  return (
    <Flex
      flexDirection={"column"}
      gap={"2px"}
      padding={{ base: "8px", md: "8px 16px" }}
      borderRadius={"8px"}
      background={"#F7F9FA"}
      border={"0.5px solid #D4D4D1"}
      // 좁은 폭에서는 세 칸이 한 줄을 나눠 쓴다.
      flex={{ base: "1 1 0", md: "0 0 auto" }}
      minWidth={{ base: 0, md: "120px" }}
    >
      <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
        {label}
      </Text>
      <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
        {value}
      </Text>
    </Flex>
  );
};

function PathPickBasketSelectionBar({
  selectedCount,
  soldOutCount,
  isOrderOverLimit,
  isOrderLoading,
  onExcludeSoldOut,
  onDeleteSelected,
  onApiSync,
  onFileRequest,
  onOrder,
}: Props) {
  const { t } = useI18nHelper();
  const { pathPickUsage } = useGetPathPickUsage();
  // 패널과 같은 규칙: API 연동 허용 플랜은 API/파일 신청을 각각, 미허용 플랜은 파일 신청만 제공한다.
  const { isApiSync, canDownload } = useMembership();

  // 좁은 폭에서는 버튼들이 한 줄에 들어가도록 짧은 문구를 쓴다.
  const isNarrow = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );

  // 바 높이는 품절 안내 유무·언어·폭에 따라 달라져 실측해 목록 끝 여백에 넘긴다.
  const rootRef = usePathPickSelectionBarHeight<HTMLDivElement>();

  const hasSoldOut = soldOutCount > 0;
  const isFileRequestDisabled = hasSoldOut || !canDownload;
  const isOrderDisabled = hasSoldOut || isOrderOverLimit;

  const requestCount = selectedCount;
  // 잔여 크레딧은 usage 응답 기준 — 무제한 플랜은 한도 표기 대신 무제한 라벨을 쓴다.
  // monthlyQuota === null도 무제한 취급(사용량 카드와 동일), 그래야 "0개"로 새지 않는다.
  const { download } = pathPickUsage;
  const isUnlimited =
    Boolean(download?.isUnlimited) || download?.monthlyQuota === null;
  const availableCredit =
    download?.remaining ??
    (download?.monthlyQuota ?? 0) - (download?.itemCount ?? 0);
  const remainingAfterRequest = Math.max(availableCredit - requestCount, 0);
  const unlimitedLabel = t("path_pick:basket.selection_bar.unlimited");

  const countValue = (count: number) =>
    t("path_pick:search_products.popular.product_count_value", { count });

  // 품절 제외는 좁은 폭(칩 줄)·넓은 폭(버튼 줄) 두 자리에 놓여 display만 다르다.
  // 비활성 스타일은 목록 헤더의 선택 삭제 버튼과 같다.
  const renderExcludeSoldOutButton = (display: {
    base: string;
    md: string;
  }) => (
    <Text
      as={"button"}
      display={display}
      textStyle={"body-sm-medium"}
      color={hasSoldOut ? "#1272EF" : "#D4D4D1"}
      cursor={hasSoldOut ? "pointer" : "not-allowed"}
      disabled={!hasSoldOut}
      onClick={() => hasSoldOut && onExcludeSoldOut()}
      _hover={hasSoldOut ? { color: "#0F62CC" } : undefined}
      flexShrink={0}
    >
      {t("path_pick:basket.selection_bar.exclude_sold_out")}
    </Text>
  );

  return (
    <Flex
      ref={rootRef}
      position={"fixed"}
      // 목록 페이지네이션(sticky) 바로 위에 띄운다.
      bottom={PATH_PICK_SELECTION_BAR_BOTTOM}
      left={"50%"}
      transform={"translateX(-50%)"}
      zIndex={PATH_PICK_SELECTION_BAR_Z_INDEX}
      // 좁은 폭에서는 720px가 화면을 넘으므로 좌우 16px 여백만 남기고 채운다.
      minWidth={{ base: "auto", md: "720px" }}
      width={{ base: "calc(100% - 32px)", md: "auto" }}
      background={"#FFFFFF"}
      border={"1px solid #1272EF"}
      borderRadius={"12px"}
      boxShadow={"0px 4px 16px rgba(0, 0, 0, 0.16)"}
      padding={{ base: "12px", md: "16px" }}
      flexDirection={"column"}
      gap={"12px"}
    >
      {/* 좁은 폭에서는 선택 요약과 크레딧 세 칸을 위아래로 둔다. */}
      <Flex
        flexDirection={{ base: "column", md: "row" }}
        gap={{ base: "8px", md: "24px" }}
        justifyContent={"space-between"}
        alignItems={{ base: "stretch", md: "center" }}
      >
        {/* 좁은 폭에서는 품절 제외를 칩 줄 오른쪽 끝으로 올려 아래 줄을 버튼에 내준다. */}
        <Flex gap={"4px"} alignItems={"center"}>
          <Flex
            height={"20px"}
            padding={"0 6px"}
            alignItems={"center"}
            justifyContent={"center"}
            borderRadius={"6px"}
            border={"0.5px solid #1272EF"}
            background={"#F0F9FF"}
            width={"fit-content"}
          >
            <Text textStyle={"caption-bold"} color={"#1272EF"}>
              {t("path_pick:basket.selected_count", { count: selectedCount })}
            </Text>
          </Flex>
          {hasSoldOut && (
            <Flex
              height={"20px"}
              padding={"0 6px"}
              alignItems={"center"}
              justifyContent={"center"}
              borderRadius={"6px"}
              border={"0.5px solid #EE7D7A"}
              background={"#FFEEEE"}
              width={"fit-content"}
            >
              <Text textStyle={"caption-bold"} color={"#CC1D1D"}>
                {t("path_pick:basket.selection_bar.sold_out_included", {
                  count: soldOutCount,
                })}
              </Text>
            </Flex>
          )}
          <Flex flex={1} display={{ base: "flex", md: "none" }} />
          {renderExcludeSoldOutButton({ base: "inline-block", md: "none" })}
        </Flex>

        <Flex gap={"8px"}>
          <StatBox
            label={t("path_pick:basket.selection_bar.available")}
            value={isUnlimited ? unlimitedLabel : countValue(availableCredit)}
          />
          <StatBox
            label={t("path_pick:basket.selection_bar.this_request")}
            value={countValue(requestCount)}
          />
          <StatBox
            label={t("path_pick:basket.selection_bar.remaining_after")}
            value={
              isUnlimited ? unlimitedLabel : countValue(remainingAfterRequest)
            }
          />
        </Flex>
      </Flex>

      {/* 좁은 폭에서는 안내 문구와 버튼 묶음을 위아래로 둔다. */}
      <Flex
        flexDirection={{ base: "column", md: "row" }}
        gap={{ base: "8px", md: "16px" }}
        justifyContent={"space-between"}
        alignItems={{ base: "stretch", md: "center" }}
      >
        {/* 품절이 섞여 있으면 차단 사유를 먼저 알린다.
            아니면 API 연동 허용 플랜(BASIC 포함)에 신청 후 양식별 다운로드 안내를 노출한다.
            좁은 폭에서는 바가 목록을 가리는 높이를 줄이려고 차단 사유만 남긴다. */}
        <Flex
          flex={1}
          minWidth={0}
          display={{ base: hasSoldOut ? "flex" : "none", md: "flex" }}
        >
          {hasSoldOut ? (
            <Text textStyle={"body-sm-medium"} color={"#CC1D1D"}>
              {t("path_pick:basket.selection_bar.sold_out_notice")}
            </Text>
          ) : (
            isApiSync && (
              <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                {t("path_pick:basket.selection_bar.note_pro")}
              </Text>
            )
          )}
        </Flex>

        {/* 좁은 폭에서는 품절 제외를 칩 줄로 올리고 짧은 문구와 줄인 좌우 패딩
            (medium 기본 32px → 10px)으로 버튼 셋을 한 줄에 둔다.
            그보다 더 좁은 기기에서만 줄바꿈되도록 wrap은 남겨 둔다. */}
        <Flex
          gap={"8px"}
          alignItems={"center"}
          justifyContent={"flex-end"}
          flexWrap={{ base: "wrap", md: "nowrap" }}
          flexShrink={0}
        >
          {/* 모바일 전용 선택 삭제 — 안내 문구가 빠진 좌측 빈자리에 붙인다.
              바는 선택이 있을 때만 뜨므로 항상 활성이고, 스타일은 목록 헤더의 선택 삭제와 같다. */}
          <Text
            as={"button"}
            display={{ base: "inline-block", md: "none" }}
            marginRight={"auto"}
            textStyle={"body-sm-medium"}
            color={"#8F8F8C"}
            onClick={onDeleteSelected}
            _hover={{ color: "#444440" }}
            flexShrink={0}
          >
            {t("path_pick:basket.list.delete_selected_short")}
          </Text>
          {renderExcludeSoldOutButton({ base: "none", md: "inline-block" })}
          {isApiSync && (
            <AppButton
              variant={"primary"}
              isDisabled={hasSoldOut}
              onClick={onApiSync}
              chakraProps={{ padding: { base: "4px 10px", md: "4px 32px" } }}
            >
              <Flex gap={"4px"} alignItems={"center"}>
                {/* 아이콘은 currentColor로 버튼 문구 색(비활성 포함)을 따른다. */}
                <CustomIcon src={"upload-cloud"} width={16} height={16} />
                <Text textStyle={"body-sm-bold"} whiteSpace={"nowrap"}>
                  {t(
                    isNarrow
                      ? "path_pick:basket.download_request.submit_api_short"
                      : "path_pick:basket.download_request.submit_api"
                  )}
                </Text>
              </Flex>
            </AppButton>
          )}
          <AppButton
            variant={"primary"}
            isDisabled={isFileRequestDisabled}
            onClick={onFileRequest}
            chakraProps={{ padding: { base: "4px 10px", md: "4px 32px" } }}
          >
            <Flex gap={"4px"} alignItems={"center"}>
              <CustomIcon src={"download"} width={16} height={16} />
              <Text textStyle={"body-sm-bold"} whiteSpace={"nowrap"}>
                {t(
                  isNarrow
                    ? "path_pick:basket.action_panel.file_request_button_short"
                    : "path_pick:basket.action_panel.file_request_button"
                )}
              </Text>
            </Flex>
          </AppButton>
          <PathPickBasketOrderLimitTooltip isActive={isOrderOverLimit}>
            <AppButton
              variant={"white"}
              isDisabled={isOrderDisabled}
              isLoading={isOrderLoading}
              onClick={onOrder}
              chakraProps={{ padding: { base: "4px 10px", md: "4px 32px" } }}
            >
              <Flex gap={"4px"} alignItems={"center"}>
                <CustomIcon
                  src={"cart"}
                  width={16}
                  height={16}
                  color={isOrderDisabled ? "#8F8F8C" : "#444440"}
                />
                <Text textStyle={"body-sm-bold"} whiteSpace={"nowrap"}>
                  {t("path_pick:basket.selection_bar.order")}
                </Text>
              </Flex>
            </AppButton>
          </PathPickBasketOrderLimitTooltip>
        </Flex>
      </Flex>
    </Flex>
  );
}

export default PathPickBasketSelectionBar;
